import React from 'react';
import { Icon, Select } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { getNumericListIds, getStringListIds } from 'Utils';
import { Field, FieldProps } from 'formik';
import { SetFieldValue } from 'Components/FormikComponents/types';
import useGetSortedLists from 'Hooks/query-hooks/global-rules/useGetSortedLists';
import styles from './ListDropdown.css';

type Props = {
    listIndex: number;
    formikList: RuleLists;
    disabled?: boolean;
    isSubmitting?: boolean;
    listError: any;
    listErrorMsg: string;
    setFieldValue: SetFieldValue;
    isReadOnly: boolean;
    listData?: ListApiGetResponse;
    parameterType?: string;
};

const ListDropdown = ({
    formikList,
    listIndex,
    disabled,
    isSubmitting,
    listError,
    listErrorMsg,
    setFieldValue,
    isReadOnly,
    listData,
    parameterType
}: Props) => {
    const { t } = useTranslation();
    const { sortedListsByType } = useGetSortedLists(listData, parameterType);

    if (!listData || _.isEmpty(listData)) return <span>{t('No Lists Found')}</span>;

    if (isReadOnly) {
        const listNamesWithIcons = formikList.listIds.map((listId: number) => {
            if (listData.listIdHashMap[listId]) {
                return (
                    <span key={listId} className={styles.listsWithIcons}>
                        <Icon name='list' size='sm' className={styles.listIcon} />
                        <span className={styles.listName}>{listData.listIdHashMap[listId].name}</span>
                    </span>
                );
            }
            // this is a tenant override where the list id belongs to the landlord, but doesn't exist in tenant lists
            return (
                <span key={listId} className={styles.listsWithIcons}>
                    <Icon name='list' size='sm' className={styles.listIcon} />
                    <span className={styles.listName}>{t('Network List')}</span>
                </span>
            );
        });

        // When a list input is read-only and listId array is empty, it means
        // clients use external lists other than those from the list management
        return (
            <span className={styles.readOnlyValue}>
                {_.isEmpty(listNamesWithIcons) ? 'External List' : listNamesWithIcons}
            </span>
        );
    }

    return (
        <Field name='lists'>
            {({ field }: FieldProps) => {
                return (
                    <Select
                        dataTestId={`${field.name}${listIndex}-field`}
                        id={`${field.name}${listIndex}`}
                        handleClickCallback={(value) =>
                            setFieldValue(`lists[${listIndex}].listIds`, getNumericListIds(value))
                        }
                        selectedItems={getStringListIds(field.value[listIndex] ?? [])}
                        className={styles.listDropDown}
                        type='listselector'
                        size='sm'
                        disabled={disabled || isSubmitting}
                        error={listError}
                        errorMsg={listErrorMsg}
                        placeholder='- Select list -'
                    >
                        {sortedListsByType.map((e) => {
                            return (
                                <option
                                    data-testid={`listOption-${e._formatted.stringId}`}
                                    key={`${e.name}`}
                                    value={e._formatted.stringId}
                                >
                                    {e.name}
                                </option>
                            );
                        })}
                    </Select>
                );
            }}
        </Field>
    );
};

export default React.memo(ListDropdown);
